import { useCallback, useContext, useMemo } from 'react'
import { useDeviceHttpProxy } from '@/hooks/DeviceHttpProxy/useDeviceHttpProxy'
import { GET_CONSUMABLE_SUPPLIES_SERVICE } from '@/constants/DeviceHttpProxy'
import { CARTRIDGE_TYPE } from '@/constants/ContentStack'
import {
  getAndParseListElementsIntoArray,
  getFirstTextContentByTag
} from '@/utils/DeviceHttpProxy'
import { ConfigContext } from '@/store'

export const CMY_COLOR_CODE = 'CMY'
export const CARTRIDGE_INK_TYPE = 'inkCartridge'
export const TANK_INK_TYPE = 'inkTank'
export const RECHARGEABLE_TONER_TYPE = 'rechargeableToner'

const CONSUMABLE_INFO_KEY = 'ccdyn:ConsumableInfo'
const CONSUMABLE_TYPE_KEY = 'dd:ConsumableTypeEnum'
const CONSUMABLE_LABEL_CODE_KEY = 'dd:ConsumableLabelCode'

function isTankOrTonerSupportedLedm(xml) {
  return getAndParseListElementsIntoArray(xml, CONSUMABLE_INFO_KEY).some(
    (e) => {
      const consumableType = getFirstTextContentByTag(e, CONSUMABLE_TYPE_KEY)
      return (
        consumableType === TANK_INK_TYPE ||
        consumableType === RECHARGEABLE_TONER_TYPE
      )
    }
  )
}

function isCMYInkSupportedLedm(xml) {
  return getAndParseListElementsIntoArray(xml, CONSUMABLE_INFO_KEY).some(
    (e) => {
      const consumableType = getFirstTextContentByTag(e, CONSUMABLE_TYPE_KEY)
      const colorCode = getFirstTextContentByTag(e, CONSUMABLE_LABEL_CODE_KEY)
      return (
        consumableType === CARTRIDGE_INK_TYPE && colorCode === CMY_COLOR_CODE
      )
    }
  )
}

function isTankOrTonerSupportedCdm(data) {
  return data.suppliesList.some(({ supplyType }) => {
    return (
      supplyType === TANK_INK_TYPE || supplyType === RECHARGEABLE_TONER_TYPE
    )
  })
}

function isCMYInkSupportedCdm(data) {
  return data.suppliesList.some(({ supplyType, supplyColorCode }) => {
    return (
      supplyType === CARTRIDGE_INK_TYPE && supplyColorCode === CMY_COLOR_CODE
    )
  })
}

export function usePrinterConsumableSupplies() {
  const { sendRequest, isCdm } = useDeviceHttpProxy()
  const { setIsLoading } = useContext(ConfigContext)

  const isTankOrTonerSupported = useCallback(
    (consumableSupplies) => {
      return isCdm
        ? isTankOrTonerSupportedCdm(consumableSupplies)
        : isTankOrTonerSupportedLedm(consumableSupplies)
    },
    [isCdm]
  )

  const isCMYInkSupported = useCallback(
    (consumableSupplies) => {
      return isCdm
        ? isCMYInkSupportedCdm(consumableSupplies)
        : isCMYInkSupportedLedm(consumableSupplies)
    },
    [isCdm]
  )

  const fetchCartridgeType = useCallback(async () => {
    setIsLoading(true)
    const { body } = await sendRequest(GET_CONSUMABLE_SUPPLIES_SERVICE)
    setIsLoading(false)
    if (isTankOrTonerSupported(body.data)) {
      return CARTRIDGE_TYPE.DEFAULT
    }
    if (isCMYInkSupported(body.data)) {
      return CARTRIDGE_TYPE.CIRCUIT
    }
    return CARTRIDGE_TYPE.CHIP
  }, [isCMYInkSupported, isTankOrTonerSupported, sendRequest, setIsLoading])

  return useMemo(
    () => ({
      fetchCartridgeType
    }),
    [fetchCartridgeType]
  )
}
